import React, { useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Modal from "../common/modal";
import Button from "../common/button";

// ─────────────────────────────────────────────
// Validation schema
// ─────────────────────────────────────────────
const getValidationSchema = (isEdit) =>
  Yup.object({
    name: Yup.string().trim().required("Name is required"),
    email: Yup.string()
      .trim()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().trim().nullable(),
    company: Yup.string().trim().nullable(),
    subject: Yup.string().trim().required("Subject is required"),
    message: Yup.string().trim().required("Message is required"),
    status: isEdit
      ? Yup.string()
          .oneOf(["pending","closed"], "Invalid status")
          .required("Status is required")
      : Yup.string().nullable(),
  });

const EMPTY_VALUES = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  message: "",
  status: "pending",
};

// ─────────────────────────────────────────────
// STATUS BADGE (view mode)
// ─────────────────────────────────────────────
const STATUS_STYLES = {
  pending:  { bg: "#fef9c3", color: "#854d0e" },
  closed:   { bg: "#fee2e2", color: "#991b1b" },
};

const StatusBadge = ({ status }) => {
  const key = String(status || "pending").toLowerCase();
  const style = STATUS_STYLES[key] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 12px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        textTransform: "capitalize",
      }}
    >
      {key}
    </span>
  );
};

// ─────────────────────────────────────────────
// FIELD COMPONENT
// ─────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div className="form-group">
    <label>{label}</label>
    {children}
  </div>
);

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const EnquiryModal = ({ isOpen, onClose, enquiry, mode, onSave, isLoading }) => {
  const isCreate = mode === "create";
  const isEdit   = mode === "edit";
  const isView   = mode === "view";
  const prevIsOpenRef = useRef(false);

  const formik = useFormik({
    enableReinitialize: isEdit && !!enquiry,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues:
      isCreate || !enquiry
        ? EMPTY_VALUES
        : {
            name:    enquiry.name    || "",
            email:   enquiry.email   || "",
            phone:   enquiry.phone   || "",
            company: enquiry.company || "",
            subject: enquiry.subject || "",
            message: enquiry.message || "",
            status:  enquiry.status  || "pending",
          },
    validationSchema: getValidationSchema(isEdit),
    onSubmit: (values) => {
      // Trim all string values before sending
      const cleaned = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
      );
      onSave && onSave(cleaned);
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) {
      prevIsOpenRef.current = false;
      return;
    }
    const justOpened = !prevIsOpenRef.current;
    prevIsOpenRef.current = true;
    if (!justOpened) return;

    if (isCreate || !enquiry) {
      formik.resetForm({ values: EMPTY_VALUES });
    } else {
      formik.resetForm({
        values: {
          name:    enquiry.name    || "",
          email:   enquiry.email   || "",
          phone:   enquiry.phone   || "",
          company: enquiry.company || "",
          subject: enquiry.subject || "",
          message: enquiry.message || "",
          status:  enquiry.status  || "pending",
        },
      });
    }
  }, [isOpen, isCreate, enquiry]);

  const handleSave = () => {
    formik.setTouched({
      name: true, email: true, phone: true,
      company: true, subject: true, message: true, status: true,
    });
    formik.handleSubmit();
  };

  const modalTitle = isView
    ? "View Enquiry"
    : isEdit
    ? "Edit Enquiry"
    : "Add Enquiry";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      <div className="form-container">

        {/* ROW 1 — Name + Email */}
        <div className="form-row">
          <Field label="Full Name *">
            {isView ? (
              <input value={formik.values.name} readOnly />
            ) : (
              <>
                <input
                  name="name"
                  placeholder="e.g. John Smith"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="error-text">{formik.errors.name}</p>
                )}
              </>
            )}
          </Field>

          <Field label="Email *">
            {isView ? (
              <input value={formik.values.email} readOnly />
            ) : (
              <>
                <input
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="error-text">{formik.errors.email}</p>
                )}
              </>
            )}
          </Field>
        </div>

        {/* ROW 2 — Phone + Company */}
        <div className="form-row">
          <Field label="Phone">
            {isView ? (
              <input value={formik.values.phone} readOnly />
            ) : (
              <>
                <input
                  name="phone"
                  placeholder="+1 (555) 000-0000"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <p className="error-text">{formik.errors.phone}</p>
                )}
              </>
            )}
          </Field>

          <Field label="Company">
            {isView ? (
              <input value={formik.values.company} readOnly />
            ) : (
              <input
                name="company"
                placeholder="Acme Corp"
                value={formik.values.company}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}
          </Field>
        </div>

        {/* Subject */}
        <Field label="Subject *">
          {isView ? (
            <input value={formik.values.subject} readOnly />
          ) : (
            <>
              <input
                name="subject"
                placeholder="What is this enquiry about?"
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.subject && formik.errors.subject && (
                <p className="error-text">{formik.errors.subject}</p>
              )}
            </>
          )}
        </Field>

        {/* Message */}
        <Field label="Message *">
          {isView ? (
            <textarea
              value={formik.values.message}
              readOnly
              rows={4}
              style={{ resize: "none", width: "100%" }}
            />
          ) : (
            <>
              <textarea
                name="message"
                placeholder="Describe the enquiry in detail..."
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                rows={4}
                style={{ resize: "vertical", width: "100%" }}
              />
              {formik.touched.message && formik.errors.message && (
                <p className="error-text">{formik.errors.message}</p>
              )}
            </>
          )}
        </Field>

        {/* Status — only shown in edit/view */}
        {(isEdit || isView) && (
          <Field label="Status">
            {isView ? (
              <StatusBadge status={formik.values.status} />
            ) : (
              <>
                <select
                  name="status"
                  className="form-field-select"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="pending">Pending</option>
                  <option value="closed">Closed</option>
                </select>
                {formik.touched.status && formik.errors.status && (
                  <p className="error-text">{formik.errors.status}</p>
                )}
              </>
            )}
          </Field>
        )}
      </div>

      {/* FOOTER */}
      <div className="modal-footer">
        <Button className="CancelButton" onClick={onClose}>
          {isView ? "Close" : "Cancel"}
        </Button>
        {!isView && (
          <Button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="CommonButton"
          >
            {isLoading
              ? "Saving..."
              : isEdit
              ? "Save Changes"
              : "Add Enquiry"}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default EnquiryModal;
